import React, { useState, useEffect, useRef, useCallback } from 'react';

const PomodoroTimer = ({ onSessionComplete, selectedTask }) => {
  const [mode, setMode] = useState('work'); // work, shortBreak, longBreak
  const [time, setTime] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [cycle, setCycle] = useState(1);
  const [totalCycles, setTotalCycles] = useState(0);
  const [settings, setSettings] = useState({
    workTime: 25,
    shortBreak: 5,
    longBreak: 15,
    cyclesBeforeLongBreak: 4,
    autoStartBreaks: false,
    autoStartWork: false
  });
  const [notifications, setNotifications] = useState(true);
  const intervalRef = useRef(null);

  const getModeTime = (currentMode) => {
    switch (currentMode) {
      case 'work': return settings.workTime * 60;
      case 'shortBreak': return settings.shortBreak * 60;
      case 'longBreak': return settings.longBreak * 60;
      default: return settings.workTime * 60;
    }
  };

  const handleSessionComplete = useCallback(() => {
    setIsRunning(false);

    // Play notification sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBSqJ2fPFdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFApGn+DyvmEcBSuJ2fPCdywGII7E8N2QQAoUXrTp66hVFA==');
    audio.play().catch(() => {});

    // Show browser notification if enabled
    if (notifications && 'Notification' in window && Notification.permission === 'granted') {
      const messages = {
        work: '🎉 Work session complete! Time for a break.',
        shortBreak: '💪 Break over! Ready for another work session?',
        longBreak: '🌟 Long break finished! Great job on completing a full cycle!'
      };
      new Notification('Study Timer', { body: messages[mode] });
    }

    let nextMode = mode;
    let nextCycle = cycle;

    if (mode === 'work') {
      // Complete work session
      const session = {
        id: Date.now(),
        subject: selectedTask?.subject || selectedTask?.title || 'Pomodoro Session',
        duration: settings.workTime * 60,
        completedAt: new Date().toISOString(),
        date: new Date().toLocaleDateString(),
        type: 'pomodoro',
        taskId: selectedTask?.id
      };
      onSessionComplete(session);

      // Update task time if task is selected
      if (selectedTask && window.updateTaskTime) {
        window.updateTaskTime(selectedTask.id, settings.workTime);
      }

      setTotalCycles(prev => prev + 1);

      // Determine next break type
      if (cycle % settings.cyclesBeforeLongBreak === 0) {
        nextMode = 'longBreak';
        nextCycle = 1;
      } else {
        nextMode = 'shortBreak';
        nextCycle = cycle + 1;
      }
    } else {
      // Complete break session
      nextMode = 'work';
    }

    setMode(nextMode);
    setCycle(nextCycle);
    setTime(getModeTime(nextMode));

    // Auto-start next session if enabled
    const shouldAutoStart =
      (nextMode !== 'work' && settings.autoStartBreaks) ||
      (nextMode === 'work' && settings.autoStartWork);

    if (shouldAutoStart) {
      setTimeout(() => setIsRunning(true), 1000);
    }
  }, [mode, cycle, settings, selectedTask, onSessionComplete, notifications]);

  useEffect(() => {
    if (isRunning && time > 0) {
      intervalRef.current = setInterval(() => {
        setTime(prevTime => prevTime - 1);
      }, 1000);
    } else if (time === 0) {
      handleSessionComplete();
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, time, handleSessionComplete]);

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const startTimer = () => setIsRunning(true);
  const pauseTimer = () => setIsRunning(false);
  const resetTimer = () => {
    setIsRunning(false);
    setTime(getModeTime(mode));
  };

  const skipSession = () => {
    setTime(0); // This will trigger handleSessionComplete
  };

  const changeMode = (newMode) => {
    setIsRunning(false);
    setMode(newMode);
    setTime(getModeTime(newMode));
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('pomodoroSettings', JSON.stringify(newSettings));

    // Update current timer if not running
    if (!isRunning) {
      setTime(getModeTime(mode));
    }
  };

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('pomodoroSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      setTime(getModeTime(mode));
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    const totalTime = getModeTime(mode);
    return ((totalTime - time) / totalTime) * 100;
  };

  const getModeConfig = () => {
    switch (mode) {
      case 'work':
        return {
          title: 'Work Session',
          color: '#dc3545',
          emoji: '💼',
          description: selectedTask ? `Working on: ${selectedTask.title}` : 'Focus time!'
        };
      case 'shortBreak':
        return {
          title: 'Short Break',
          color: '#28a745',
          emoji: '☕',
          description: 'Take a quick breather'
        };
      case 'longBreak':
        return {
          title: 'Long Break',
          color: '#17a2b8',
          emoji: '🌟',
          description: 'Well deserved long break!'
        };
      default:
        return { title: 'Pomodoro', color: '#6c757d', emoji: '🍅', description: '' };
    }
  };

  const modeConfig = getModeConfig();

  return (
    <div className="pomodoro-timer">
      <div className="pomodoro-header">
        <h2>🍅 Pomodoro Technique</h2>
        <div className="cycle-info">
          <span className="cycle-text">Cycle {cycle}</span>
          <span className="total-cycles">Total: {totalCycles}</span>
        </div>
      </div>

      <div className="mode-selector">
        <button
          onClick={() => changeMode('work')}
          className={`mode-btn ${mode === 'work' ? 'active' : ''}`}
          disabled={isRunning}
        >
          💼 Work ({settings.workTime}m)
        </button>
        <button
          onClick={() => changeMode('shortBreak')}
          className={`mode-btn ${mode === 'shortBreak' ? 'active' : ''}`}
          disabled={isRunning}
        >
          ☕ Short Break ({settings.shortBreak}m)
        </button>
        <button
          onClick={() => changeMode('longBreak')}
          className={`mode-btn ${mode === 'longBreak' ? 'active' : ''}`}
          disabled={isRunning}
        >
          🌟 Long Break ({settings.longBreak}m)
        </button>
      </div>

      <div className="timer-display">
        <div className="mode-info">
          <h3>{modeConfig.emoji} {modeConfig.title}</h3>
          <p className="mode-description">{modeConfig.description}</p>
        </div>

        <div className="progress-circle-container">
          <svg width="220" height="220" className="progress-svg">
            <circle
              cx="110"
              cy="110"
              r="100"
              stroke="#e6e6e6"
              strokeWidth="8"
              fill="transparent"
            />
            <circle
              cx="110"
              cy="110"
              r="100"
              stroke={modeConfig.color}
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={`${2 * Math.PI * 100}`}
              strokeDashoffset={`${2 * Math.PI * 100 * (1 - getProgressPercentage() / 100)}`}
              transform="rotate(-90 110 110)"
              className="progress-circle"
            />
          </svg>
          <div className="time-display">{formatTime(time)}</div>
        </div>
      </div>

      <div className="timer-controls">
        <div className="main-controls">
          {!isRunning ? (
            <button onClick={startTimer} className="start-btn" disabled={time === 0}>
              ▶ Start
            </button>
          ) : (
            <button onClick={pauseTimer} className="pause-btn">
              ⏸ Pause
            </button>
          )}
          <button onClick={resetTimer} className="reset-btn">
            🔄 Reset
          </button>
          <button onClick={skipSession} className="skip-btn">
            ⏭ Skip
          </button>
        </div>
      </div>

      <div className="pomodoro-settings">
        <h4>Settings</h4>
        <div className="settings-grid">
          <div className="setting-item">
            <label>Work Time (min):</label>
            <input
              type="number"
              min="1"
              max="60"
              value={settings.workTime}
              onChange={(e) => updateSettings({
                ...settings,
                workTime: parseInt(e.target.value) || 25
              })}
              disabled={isRunning}
            />
          </div>
          <div className="setting-item">
            <label>Short Break (min):</label>
            <input
              type="number"
              min="1"
              max="30"
              value={settings.shortBreak}
              onChange={(e) => updateSettings({
                ...settings,
                shortBreak: parseInt(e.target.value) || 5
              })}
              disabled={isRunning}
            />
          </div>
          <div className="setting-item">
            <label>Long Break (min):</label>
            <input
              type="number"
              min="1"
              max="60"
              value={settings.longBreak}
              onChange={(e) => updateSettings({
                ...settings,
                longBreak: parseInt(e.target.value) || 15
              })}
              disabled={isRunning}
            />
          </div>
          <div className="setting-item">
            <label>Cycles before long break:</label>
            <input
              type="number"
              min="2"
              max="8"
              value={settings.cyclesBeforeLongBreak}
              onChange={(e) => updateSettings({
                ...settings,
                cyclesBeforeLongBreak: parseInt(e.target.value) || 4
              })}
              disabled={isRunning}
            />
          </div>
        </div>

        <div className="auto-settings">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.autoStartBreaks}
              onChange={(e) => updateSettings({
                ...settings,
                autoStartBreaks: e.target.checked
              })}
            />
            Auto-start breaks
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.autoStartWork}
              onChange={(e) => updateSettings({
                ...settings,
                autoStartWork: e.target.checked
              })}
            />
            Auto-start work sessions
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={notifications}
              onChange={(e) => setNotifications(e.target.checked)}
            />
            Browser notifications
          </label>
        </div>
      </div>

      {selectedTask && (
        <div className="selected-task-info">
          <h4>Currently working on:</h4>
          <div className="task-preview">
            <span className="task-title">{selectedTask.title}</span>
            {selectedTask.subject && (
              <span className="task-subject">{selectedTask.subject}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PomodoroTimer;